import React, { useEffect, useState } from 'react';
import { Controller, Control } from 'react-hook-form';
import { Checkbox, Radio, theme } from 'antd';
import { ErrorText } from '../Elements';
import FormLabel from './FormLabel';

const { useToken } = theme;

interface RadioGroupFieldProps {
  label?: string;
  control: Control<any>;
  name: string;
  options: Array<{
    label: string;
    value: any;
    disabled?: boolean;
    icon?: string;
  }>;
  error?: string;
  formDirection?: 'row' | 'column';
  cols?: {
    sm: number;
    md: number;
    lg: number;
  };
  size?: 'small' | 'middle' | 'large';
  iconMapper?: Record<string, string>;
  multiple?: boolean;
  disabled?: boolean;
}

const RadioGroupField: React.FC<RadioGroupFieldProps> = ({
  label,
  control,
  name,
  options,
  error,
  formDirection = 'column',
  cols = { sm: 1, md: 1, lg: 2 },
  size = 'small',
  iconMapper,
  multiple = false,
  disabled = false,
}) => {
  const [currentCols, setCurrentCols] = useState(cols.lg);
  const { token } = useToken();

  useEffect(() => {
    const updateCols = () => {
      const width = window.innerWidth;
      if (width < 600) {
        setCurrentCols(cols.sm);
      } else if (width < 1024) {
        setCurrentCols(cols.md);
      } else {
        setCurrentCols(cols.lg);
      }
    };

    updateCols();
    window.addEventListener('resize', updateCols);
    return () => window.removeEventListener('resize', updateCols);
  }, [cols]);

  return (
    <div className={`form-control ${formDirection}`}>
      <div className="form-label-wrap">
        {label && (
          <label
            className="form-label"
            style={{ color: token.colorTextSecondary }}
          >
            <FormLabel label={label} />
          </label>
        )}
      </div>
      <div className="input-containter">
        {!multiple ? (
          <Controller
            name={name}
            disabled={disabled}
            control={control}
            render={({ field }) => (
              <Radio.Group
                {...field}
                style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${currentCols}, 1fr)`,
                  gap: '20px',
                }}
              >
                {options.map((option) => (
                  <Radio
                    className="custom-radio"
                    style={{
                      padding:
                        size === 'small'
                          ? '4px 8px'
                          : size === 'middle'
                            ? '6px 10px'
                            : '10px 14px',
                    }}
                    key={option.value}
                    value={option.value}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1.5rem',
                      }}
                    >
                      {option?.icon && iconMapper && (
                        <img
                          style={{ width: '1.8rem', height: '1.8rem' }}
                          src={iconMapper[option.icon]}
                          alt={option.label}
                        />
                      )}
                      {option.label}
                    </div>
                  </Radio>
                ))}
              </Radio.Group>
            )}
          />
        ) : (
          <Controller
            name={name}
            control={control}
            render={({ field }) => (
              <Checkbox.Group
                {...field}
                style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${currentCols}, 1fr)`,
                  gap: '20px',
                }}
              >
                {options.map((option) => (
                  <Checkbox
                    className="custom-checkbox"
                    style={{
                      padding:
                        size === 'small'
                          ? '8px 12px'
                          : size === 'middle'
                            ? '10px 14px'
                            : '12px 16px',
                    }}
                    key={option.value}
                    value={option.value}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1.5rem',
                      }}
                    >
                      {option?.icon && iconMapper && (
                        <img
                          style={{ width: '1.8rem', height: '1.8rem' }}
                          src={iconMapper[option.icon]}
                          alt={option.label}
                        />
                      )}
                      {option.label}
                    </div>
                  </Checkbox>
                ))}
              </Checkbox.Group>
            )}
          />
        )}
        {error && <ErrorText error={error} />}
      </div>
    </div>
  );
};

export default RadioGroupField;
